
import { mixins, Options } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { ShopifyCart } from '@/api/shopify/ShopifyCart';
import LoadingScreen from '@/components/LoadingScreen.vue';
import MButton from '@/components/MButton.vue';
import CheckoutWidgetMixin from '@/mixins/checkoutWidget';

declare global {
  interface Window {
    ManifoldShopifyURL?: string;
  }
}

@Options({
  components: {
    MButton,
    LoadingScreen
  }
})
export default class MCheckout extends mixins(CheckoutWidgetMixin) {
  pendingDraftOrder = '';
  standalone = false;
  isCheckingOut = false;
  isClearingCart = false;

  @Watch('state.storedShopifyCart')
  async cartItemsWatcher() {
    if (this.state.storedShopifyCart && this.state.storedShopifyCart.pendingDraftOrder) {
      this.checkoutText = this.alreadyExistingCheckoutMessage
        ? this.alreadyExistingCheckoutMessage
        : 'Already existing checkout, click here to complete.';
      this.pendingDraftOrder = this.state.storedShopifyCart.pendingDraftOrderUrl;
    }
  }

  redirectToExistingCheckout() {
    window.location.href = this.pendingDraftOrder;
  }

  async clearCart() {
    this.isClearingCart = true;
    try {
      const currentShop = await this.state.ShopifyAPIClient.getCurrentShop();
      const oauthToken = await this.getOAuth();

      await this.state.RedeemAPIClient.clearCart(oauthToken, currentShop);
      await this.state.ShopifyAPIClient.clearBrowserCart(this.cartIconSection);
      window.location.reload();
    } catch (e) {
      console.error('Error clearing cart: ', e);
    }

    this.isClearingCart = false;
  }

  async checkoutCart() {
    this.isCheckingOut = true;

    try {
      const frontendCart = await this.state.ShopifyAPIClient.getBrowserCart();
      const currentShop = await this.state.ShopifyAPIClient.getCurrentShop();

      // Make sure server and client carts are in sync
      const shopifyCart = new ShopifyCart(frontendCart);
      await shopifyCart.initialSyncWithTokenCart(
        this.state.storedShopifyCart,
        this.cartIconSection
      );

      const oauthToken = await this.getOAuth();

      const res = await this.state.RedeemAPIClient.checkoutRedeemCart(
        oauthToken,
        currentShop,
        frontendCart
      );
      // After checking out on our end
      await this.state.ShopifyAPIClient.clearBrowserCart(this.cartIconSection);
      let redirectLink;
      if (res.draft_order && res.draft_order.body) {
        redirectLink = getInvoiceRedirectLink(
          res.draft_order.body.invoice_url,
          res.draft_order.body.id
        );
      } else {
        redirectLink = getInvoiceRedirectLink(res.invoice_url, res.id);
      }
      window.location.href = redirectLink;
    } catch (e: any) {
      if (e.statusCode === 409 && e.message && e.message.includes('pending')) {
        this.checkoutText = 'Checkout already in progress. Cannot checkout twice.';
      } else if (e.statusCode === 422) {
        this.checkoutText = 'Problem with items in your cart. Please clear your cart.';
      } else {
        this.checkoutText = 'Problem with checkout. Please clear your cart and try again.';
      }
      if (e.message) {
        alert(`Checkout error: ${e.message}`);
      } else {
        alert(`Unknown error encountered during checkout. Please clear your cart and try again.`);
      }
      this.isCheckingOut = false;
    }
  }

  get disabled() {
    return (
      (this.state && this.state.wrongChain) ||
      (!this.state && this.state.walletAddress) ||
      this.isClearingCart ||
      this.isCheckingOut
    );
  }

  get clearCartTextFn() {
    if (!this.state && this.state.walletAddress) {
      return this.connectWalletText ? this.connectWalletText : 'Connect Wallet to Clear Cart';
    }
    return this.clearCartText ? this.clearCartText : 'Clear Cart';
  }

  get buttonText() {
    if (!this.state && this.state.walletAddress) {
      return this.connectWalletText ? this.connectWalletText : 'Connect Wallet to Checkout';
    }
    return this.checkoutText ? this.checkoutText : 'Checkout with NFTs';
  }
}

const getInvoiceRedirectLink = (invoiceUrl: string, draftOrderId: string) => {
  if (typeof window.ManifoldShopifyURL === 'string') {
    return window.ManifoldShopifyURL.replaceAll(
      '<INVOICE_URL>',
      encodeURIComponent(invoiceUrl)
    ).replaceAll('<DRAFT_ORDER_ID>', draftOrderId);
  }

  return invoiceUrl;
};
